import { forwardRef, memo, useState, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { MobileMenu } from './MobileMenu';
import type { NavbarVariant, NavbarBaseProps, NavItem } from './Navbar.types';
import type { BaseComponentProps } from '../../types';

export interface NavbarProps extends NavbarBaseProps, BaseComponentProps {
  /**
   * Variante del navbar
   * @default 'classic'
   */
  variant?: NavbarVariant;
}

/**
 * Navbar Component
 * 
 * Navbar responsive con 5 varianti diverse.
 * Include menu mobile automatico.
 * Ottimizzato con React.memo e useCallback per performance.
 * 
 * @example
 * ```tsx
 * <Navbar
 *   variant="classic"
 *   logo={<Logo />}
 *   menuItems={[
 *     { label: 'Home', href: '/' },
 *     { label: 'About', href: '/about' }
 *   ]}
 *   actions={<Button>Login</Button>}
 *   sticky
 * />
 * ```
 */
const NavbarComponent = forwardRef<HTMLElement, NavbarProps>(
  (
    {
      className,
      variant = 'classic',
      logo,
      logoHref = '/',
      menuItems,
      actions,
      sticky = false,
      transparent = false,
      shadow = true,
      mobileBreakpoint = 'lg',
      onMobileMenuToggle,
      ...props
    },
    ref
  ) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const toggleMobileMenu = useCallback(() => {
      setIsMobileMenuOpen((prev) => {
        const newState = !prev;
        onMobileMenuToggle?.(newState);
        return newState;
      });
    }, [onMobileMenuToggle]);

    const closeMobileMenu = useCallback(() => {
      setIsMobileMenuOpen(false);
    }, []);

    const breakpointClass = {
      sm: 'sm:hidden',
      md: 'md:hidden',
      lg: 'lg:hidden',
    }[mobileBreakpoint];

    const breakpointShowClass = {
      sm: 'sm:flex',
      md: 'md:flex',
      lg: 'lg:flex',
    }[mobileBreakpoint];

    // Variant: Classic
    if (variant === 'classic') {
      return (
        <>
          <nav
            ref={ref}
            className={cn(
              'w-full transition-all duration-200',
              sticky && 'sticky top-0 z-40',
              transparent ? 'bg-transparent' : 'bg-base-100',
              shadow && 'shadow-md border-b border-base-300',
              className
            )}
            {...props}
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-16">
                {/* Logo */}
                <a href={logoHref} className="flex-shrink-0 font-bold text-xl text-base-content">
                  {logo}
                </a>

                {/* Desktop Menu */}
                <div className={cn('hidden gap-6 items-center', breakpointShowClass)}>
                  {menuItems.map((item) => (
                    <div key={item.label} className="relative">
                      {item.children ? (
                        // Dropdown
                        <div
                          onMouseEnter={() => setOpenDropdown(item.label)}
                          onMouseLeave={() => setOpenDropdown(null)}
                        >
                          <button className="flex items-center gap-1 px-3 py-2 text-base-content hover:text-primary transition-colors">
                            {item.label}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {openDropdown === item.label && (
                            <div className="absolute top-full left-0 mt-2 w-48 bg-base-100 border border-base-300 rounded-lg shadow-lg py-2">
                              {item.children.map((child) => (
                                <a
                                  key={child.label}
                                  href={child.href}
                                  className="block px-4 py-2 text-base-content hover:bg-base-200 transition-colors"
                                >
                                  {child.label}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <a
                          href={item.href}
                          className="px-3 py-2 text-base-content hover:text-primary transition-colors"
                        >
                          {item.label}
                          {item.badge && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-primary text-primary-content rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </a>
                      )}
                    </div>
                  ))}
                </div>

                {/* Actions + Mobile Menu Button */}
                <div className="flex items-center gap-4">
                  <div className={cn('hidden', breakpointShowClass)}>
                    {actions}
                  </div>
                  
                  <button
                    onClick={toggleMobileMenu}
                    className={cn('p-2 hover:bg-base-200 rounded-lg transition-colors', breakpointClass)}
                    aria-label="Menu"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            items={menuItems}
            actions={actions}
          />
        </>
      );
    }

    // Variant: Centered
    if (variant === 'centered') {
      return (
        <>
          <nav
            ref={ref}
            className={cn(
              'w-full transition-all duration-200',
              sticky && 'sticky top-0 z-40',
              transparent ? 'bg-transparent' : 'bg-base-100',
              shadow && 'shadow-md border-b border-base-300',
              className
            )}
            {...props}
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-16">
                {/* Left Menu */}
                <div className={cn('hidden gap-4 flex-1', breakpointShowClass)}>
                  {menuItems.slice(0, Math.floor(menuItems.length / 2)).map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="px-3 py-2 text-base-content hover:text-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>

                {/* Centered Logo */}
                <a href={logoHref} className="flex-shrink-0 font-bold text-xl text-base-content">
                  {logo}
                </a>

                {/* Right Menu */}
                <div className={cn('hidden gap-4 flex-1 justify-end', breakpointShowClass)}>
                  {menuItems.slice(Math.floor(menuItems.length / 2)).map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="px-3 py-2 text-base-content hover:text-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                  {actions}
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className={cn('p-2 hover:bg-base-200 rounded-lg transition-colors ml-auto', breakpointClass)}
                  aria-label="Menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </nav>

          <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            items={menuItems}
            actions={actions}
          />
        </>
      );
    }

    // Variant: Sidebar (versione semplificata - toggle laterale)
    if (variant === 'sidebar') {
      return (
        <>
          <nav
            ref={ref}
            className={cn(
              'w-full transition-all duration-200',
              transparent ? 'bg-transparent' : 'bg-base-100',
              shadow && 'shadow-md border-b border-base-300',
              className
            )}
            {...props}
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center gap-4">
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 hover:bg-base-200 rounded-lg transition-colors"
                    aria-label="Menu"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <a href={logoHref} className="font-bold text-xl text-base-content">
                    {logo}
                  </a>
                </div>
                <div>{actions}</div>
              </div>
            </div>
          </nav>

          <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            items={menuItems}
            actions={actions}
          />
        </>
      );
    }

    // Variant: Mega (con mega menu dropdown)
    if (variant === 'mega') {
      return (
        <>
          <nav
            ref={ref}
            className={cn(
              'w-full transition-all duration-200',
              sticky && 'sticky top-0 z-40',
              transparent ? 'bg-transparent' : 'bg-base-100',
              shadow && 'shadow-md border-b border-base-300',
              className
            )}
            {...props}
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-16">
                <a href={logoHref} className="flex-shrink-0 font-bold text-xl text-base-content">
                  {logo}
                </a>

                {/* Desktop Mega Menu */}
                <div className={cn('hidden gap-6 items-center', breakpointShowClass)}>
                  {menuItems.map((item) => (
                    <div key={item.label} className="relative group">
                      {item.children ? (
                        <div>
                          <button className="flex items-center gap-1 px-3 py-2 text-base-content hover:text-primary transition-colors">
                            {item.label}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {/* Mega Menu Dropdown */}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-screen max-w-4xl bg-base-100 border border-base-300 rounded-lg shadow-2xl p-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                            <div className="grid grid-cols-3 gap-8">
                              {item.children.map((child) => (
                                <a
                                  key={child.label}
                                  href={child.href}
                                  className="block p-4 hover:bg-base-200 rounded-lg transition-colors"
                                >
                                  <div className="font-semibold text-base-content mb-1">{child.label}</div>
                                  <div className="text-sm text-base-content/60">Descrizione breve</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <a
                          href={item.href}
                          className="px-3 py-2 text-base-content hover:text-primary transition-colors"
                        >
                          {item.label}
                        </a>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <div className={cn('hidden', breakpointShowClass)}>
                    {actions}
                  </div>
                  <button
                    onClick={toggleMobileMenu}
                    className={cn('p-2 hover:bg-base-200 rounded-lg transition-colors', breakpointClass)}
                    aria-label="Menu"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            items={menuItems}
            actions={actions}
          />
        </>
      );
    }

    // Variant: Minimal
    if (variant === 'minimal') {
      return (
        <>
          <nav
            ref={ref}
            className={cn(
              'w-full transition-all duration-200',
              sticky && 'sticky top-0 z-40',
              transparent ? 'bg-transparent' : 'bg-base-100',
              shadow && 'border-b border-base-300',
              className
            )}
            {...props}
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-14">
                <a href={logoHref} className="font-bold text-lg text-base-content">
                  {logo}
                </a>
                
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 hover:bg-base-200 rounded transition-colors"
                  aria-label="Menu"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </nav>

          <MobileMenu
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            items={menuItems}
            actions={actions}
          />
        </>
      );
    }

    return null;
  }
);

NavbarComponent.displayName = 'Navbar';

export const Navbar = memo(NavbarComponent);

// Re-export types
export type { NavbarVariant, NavItem } from './Navbar.types';

